import { CANCELLATION_POLICY_OPTIONS } from '~/constants/booking'

/**
 * Counts calendar days until the booking starts, using today's date in the booking timezone.
 *
 * @param startDate The booking date; ISO timestamps are normalized to their UTC calendar date.
 * @param timezone The RV or booking IANA timezone.
 */
function getDaysUntilStartDate(startDate: string, timezone: string) {
  // Compare date-only values in UTC so daylight-saving offsets cannot change the day count.
  const startDay = datetime.utc(startDate).startOf('day')
  const today = datetime.utc(datetime().tz(timezone).format('YYYY-MM-DD'))

  return startDay.diff(today, 'days')
}

/**
 * Returns the cancellation policy based on the number of calendar days until the start date in the booking timezone.
 *
 * @param startDate The booking date; ISO timestamps are normalized to their UTC calendar date.
 * @param timezone The RV or booking IANA timezone; UTC is used when unavailable.
 * @returns The cancellation policy: up to 7 days, 8–30 days, more than 30 days.
 */
export function getCancellationPolicyByStartDate(startDate?: string, timezone = 'UTC'): CANCELLATION_POLICY_OPTIONS {
  if (!startDate) return CANCELLATION_POLICY_OPTIONS.NoDatesSelected

  const diffDays = getDaysUntilStartDate(startDate, timezone)

  if (diffDays <= 7) return CANCELLATION_POLICY_OPTIONS.LessThan8Days
  if (diffDays <= 30) return CANCELLATION_POLICY_OPTIONS.Between8To30Days

  return CANCELLATION_POLICY_OPTIONS.MoreThan30Days
}

/**
 * Returns whether the 24-hour grace period applies for a trip starting on the given date.
 * Grace period is offered when the trip starts at least two calendar days away in the booking timezone.
 *
 * @param startDate The booking date; ISO timestamps are normalized to their UTC calendar date.
 * @param timezone The RV or booking IANA timezone; UTC is used when unavailable.
 * @returns True if the grace period applies.
 *
 * @example isGracePeriodEligible('2020-04-01') // false (today)
 * @example isGracePeriodEligible('2020-04-02') // false (tomorrow)
 * @example isGracePeriodEligible('2020-04-03') // true (2+ nights away)
 */
export function isGracePeriodEligible(startDate?: string, timezone = 'UTC') {
  if (!startDate) return false

  return getDaysUntilStartDate(startDate, timezone) > 1
}
